import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Download, PencilRuler, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import BeachMap from "@/components/BeachMap";
import StatusLegend from "@/components/StatusLegend";
import RefreshBar from "@/components/RefreshBar";
import AdminBaldachinDrawer from "@/components/AdminBaldachinDrawer";
import { InlineLoader } from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS_FILTERS } from "@/lib/status";
import { downloadReport } from "@/lib/download";

export default function LiveMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [workerFilter, setWorkerFilter] = useState("all");
  const [view, setView] = useState("detailed");
  const [workers, setWorkers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback((spin = false) => {
    if (spin) setLoading(true);
    api
      .get("/map")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(true);
    api.get("/workers").then((r) => setWorkers(r.data)).catch(() => {});
  }, [load]);

  const baldachins = data?.baldachins || [];
  const q = search.trim();
  const matches = useCallback(
    (b) => {
      if (filter !== "all" && b.status !== filter) return false;
      if (workerFilter !== "all" && !(b.worker_ids_involved || []).includes(workerFilter)) return false;
      if (q && !String(b.number).includes(q)) return false;
      return true;
    },
    [filter, workerFilter, q]
  );
  const anyFilter = filter !== "all" || workerFilter !== "all" || q.length > 0;
  const dimIds = useMemo(
    () => (anyFilter ? new Set(baldachins.filter((b) => !matches(b)).map((b) => b.id)) : null),
    [anyFilter, baldachins, matches]
  );
  const visibleCount = baldachins.filter(matches).length;

  if (loading && !data) return <InlineLoader label="Loading live map…" />;

  return (
    <div className="space-y-4 bb-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Live Map</h1>
          <p className="text-sm text-stone-500">Real-time beach status · {visibleCount} of {baldachins.length} shown</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RefreshBar lastUpdated={data?.last_updated} onRefresh={() => load(true)} loading={loading} testid="livemap-refresh" />
          <Button variant="outline" onClick={() => downloadReport(data?.date)} data-testid="livemap-export-btn" className="gap-2 rounded-xl font-semibold">
            <Download className="h-4 w-4" /> Export Today
          </Button>
          <Button onClick={() => navigate("/admin/map-editor")} data-testid="livemap-edit-layout-btn" className="gap-2 rounded-xl font-semibold">
            <PencilRuler className="h-4 w-4" /> Edit Map Layout
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-stone-200 bg-white p-3">
        <div className="relative min-w-[180px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input
            data-testid="livemap-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search number…"
            className="h-10 rounded-xl pl-9"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`livemap-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`rounded-lg px-3 py-2 text-xs font-bold transition ${filter === f.key ? "bg-primary text-white" : "bg-stone-100 text-stone-600"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <Select value={workerFilter} onValueChange={setWorkerFilter}>
          <SelectTrigger className="h-10 w-[160px] rounded-xl" data-testid="livemap-worker-filter"><SelectValue placeholder="Worker" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All workers</SelectItem>
            {workers.map((w) => (
              <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex overflow-hidden rounded-xl border border-stone-200">
          <button
            data-testid="livemap-view-compact"
            onClick={() => setView("compact")}
            className={`px-3 py-2 text-xs font-bold transition ${view === "compact" ? "bg-stone-900 text-white" : "bg-white text-stone-600"}`}
          >
            Compact
          </button>
          <button
            data-testid="livemap-view-detailed"
            onClick={() => setView("detailed")}
            className={`px-3 py-2 text-xs font-bold transition ${view === "detailed" ? "bg-stone-900 text-white" : "bg-white text-stone-600"}`}
          >
            Detailed
          </button>
        </div>
      </div>

      <StatusLegend />

      {anyFilter && visibleCount === 0 && (
        <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700" data-testid="livemap-no-match">
          <AlertTriangle className="h-4 w-4" /> No baldachins match the current filters.
        </div>
      )}

      <BeachMap
        baldachins={baldachins}
        mode={view}
        dimIds={dimIds}
        selectedId={selectedId}
        onCardClick={(b) => { setSelectedId(b.id); setDrawerOpen(true); }}
        minHeight={560}
        initialZoom={0.8}
      />

      <AdminBaldachinDrawer
        baldachinId={selectedId}
        date={data?.date}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onChanged={() => load()}
      />
    </div>
  );
}
